/**
 * Galaxy generation.
 *
 * Radius-4 pointy-top hex map → 61 systems. Generation is a five-step pipeline:
 *
 *   1. lay out every hex and tag it with its ring/region
 *   2. plant the four homeworlds on ring 4 at perimeter indices 0/6/12/18
 *   3. draw a class for every other hex from a ring-weighted table
 *   4. seed relics, with the fattest prize at the galactic centre
 *   5. run a **fairness pass** that swaps classes until each homeworld's local
 *      neighbourhood is worth roughly the same
 *
 * Step 5 matters more than it sounds. Pure weighted random regularly hands one seat
 * three gas giants at arm's length and another seat a wall of deep space, which in a
 * 60-minute game is not recoverable. The pass is deterministic given the seed.
 *
 * The hex grid has 6-fold symmetry, so exact 4-fold mirroring is impossible; equalising
 * measured local value is the honest alternative. The four homeworlds do sit in a
 * perfectly symmetric arrangement though: adjacent seats are 6 hexes apart, opposite
 * seats 8, and every seat is exactly 4 from the centre.
 */

import {
  COLONISABLE_CLASSES, HOMEWORLD_YIELD, MAP_RADIUS, RELIC_COUNT, SYSTEM_CLASSES, regionForRing,
} from './constants';
import { distance, key, parseKey, ring, ringOf, spiral } from './hex';
import type { Rng } from './rng';
import type { Axial, HexKey, PlayerId, RelicKind, StarSystem, SystemClass } from './types';

/** Perimeter indices on ring 4 used as homeworlds. Evenly spaced, provably fair. */
const HOMEWORLD_RING_INDICES: readonly number[] = [0, 6, 12, 18];

/** Homeworlds are always this class, so nobody opens on a rock. */
const HOMEWORLD_CLASS: SystemClass = 'terran';

/** Relative worth of one unit of each resource, for the fairness metric. */
const RESOURCE_VALUE = { ore: 1.0, fuel: 1.0, biomass: 1.2, data: 1.35 } as const;

/** Radius of the "neighbourhood" the fairness pass equalises. */
const FAIRNESS_RADIUS = 2;

/** How close neighbourhood values must get before the pass stops. */
const FAIRNESS_TOLERANCE = 1.6;
const FAIRNESS_MAX_SWAPS = 240;

export interface Galaxy {
  systems: Record<HexKey, StarSystem>;
  /** Homeworld key per seat, index-matched to PlayerId. */
  homeworlds: HexKey[];
}

/* --------------------------------------------------------------- class weights */

/**
 * Ring-dependent draw weights.
 *
 * The Core is deliberately rich and nasty (anomalies and volcanoes, no deep space) so
 * that pushing inward is a real decision. The Outer Rim is thin, which is what makes
 * expanding off your homeworld urgent.
 */
function classWeight(cls: SystemClass, ring: number): number {
  const base = SYSTEM_CLASSES[cls].weight;

  if (cls === 'deepSpace') {
    // No voids in the Core; plenty in the Outer Rim to open up lanes.
    if (ring <= 1) return 0;
    if (ring === 4) return base * 1.7;
    return base;
  }

  switch (ring) {
    case 0:
    case 1:
      // Core: reward the risk.
      if (cls === 'anomaly') return base * 3.2;
      if (cls === 'volcanic') return base * 1.8;
      if (cls === 'terran') return base * 0.5;
      return base * 0.8;
    case 2:
    case 3:
      // Mid Rim is the balanced middle — leave the table as authored.
      return base;
    default:
      // Outer Rim: poorer, safer, mostly ice and rock.
      if (cls === 'terran') return base * 0.4;
      if (cls === 'anomaly') return base * 0.4;
      if (cls === 'oceanic') return base * 0.6;
      if (cls === 'ice' || cls === 'asteroid') return base * 1.4;
      return base;
  }
}

/* ------------------------------------------------------------------- helpers */

function makeSystem(coord: Axial, cls: SystemClass, artSeed: number): StarSystem {
  const def = SYSTEM_CLASSES[cls];
  const r = ringOf(coord);
  return {
    id: key(coord),
    coord: { q: coord.q, r: coord.r },
    ring: r,
    region: regionForRing(r),
    cls,
    yield: { ...def.yield },
    habitability: def.habitability,
    hazard: def.hazard,
    surveyed: false,
    owner: null,
    development: 'none',
    relic: null,
    relicTaken: false,
    artSeed,
    blockedFromRound: 0,
    blockedUntilRound: 0,
    megastructure: null,
  };
}

/** Rewrite a system in place to a different class, preserving identity and art seed. */
function reclass(system: StarSystem, cls: SystemClass): void {
  const def = SYSTEM_CLASSES[cls];
  system.cls = cls;
  system.yield = { ...def.yield };
  system.habitability = def.habitability;
  system.hazard = def.hazard;
}

/** Worth of a single system, discounted for hazard and for being hard to develop. */
function systemValue(system: StarSystem): number {
  if (system.cls === 'deepSpace') return 0;
  const y = system.yield;
  const raw =
    y.ore * RESOURCE_VALUE.ore +
    y.fuel * RESOURCE_VALUE.fuel +
    y.biomass * RESOURCE_VALUE.biomass +
    y.data * RESOURCE_VALUE.data;

  // Habitability 2+ means it can become a colony and double, so it is worth more than
  // its printed yield; hazard is a real tax in both Ore and event exposure.
  const habFactor = system.habitability >= 2 ? 1.45 : 1.0;
  const hazardTax = system.hazard * 0.28;
  return Math.max(0, raw * habFactor - hazardTax);
}

/**
 * Value of everything within `FAIRNESS_RADIUS` of a homeworld, weighted by how close
 * it is — a gas giant next door is worth more than one two jumps out.
 */
function neighbourhoodValue(
  systems: Record<HexKey, StarSystem>,
  homeworld: HexKey,
): number {
  const origin = parseKey(homeworld);
  let total = 0;
  for (const sys of Object.values(systems)) {
    const d = distance(origin, sys.coord);
    if (d === 0 || d > FAIRNESS_RADIUS) continue;
    total += systemValue(sys) / d;
  }
  return total;
}

/* ---------------------------------------------------------------- generation */

export function generateGalaxy(rng: Rng): Galaxy {
  const systems: Record<HexKey, StarSystem> = {};
  const coords = spiral(MAP_RADIUS);

  // 1 + 2 — homeworld sites first, so step 3 can avoid them.
  const outerRing = ring(MAP_RADIUS);
  const homeworlds = HOMEWORLD_RING_INDICES.map((i) => key(outerRing[i]!));
  const homeworldSet = new Set(homeworlds);

  // 3 — draw a class for every hex.
  for (const coord of coords) {
    const k = key(coord);
    const artSeed = rng.int(0x7fffffff);

    if (homeworldSet.has(k)) {
      const home = makeSystem(coord, HOMEWORLD_CLASS, artSeed);
      // A capital is developed infrastructure, not raw Terran biosphere — it produces a
      // little of everything so no seat is locked out of a resource from turn one.
      home.yield = { ...HOMEWORLD_YIELD };
      systems[k] = home;
      continue;
    }

    const r = ringOf(coord);
    const candidates: SystemClass[] = [...COLONISABLE_CLASSES, 'deepSpace'];
    const cls = rng.weighted(candidates, (c) => classWeight(c, r));
    systems[k] = makeSystem(coord, cls, artSeed);
  }

  // Guarantee each homeworld has at least two developable neighbours, otherwise the
  // opening is a coin-flip on whether you can expand at all.
  for (const home of homeworlds) {
    ensureViableStart(systems, home, rng);
  }

  // The galactic centre is always an Anomaly — the single richest, nastiest hex, and
  // the thing the Core majority award is really fighting over.
  const centre = systems['0,0'];
  if (centre) {
    reclass(centre, 'anomaly');
    centre.relic = 'archive';
  }

  // 4 — relics.
  seedRelics(systems, rng, homeworldSet);

  // 5 — fairness.
  balanceStarts(systems, homeworlds, rng);

  // Starting knowledge: the whole Outer Rim is charted, everything inward is dark.
  for (const sys of Object.values(systems)) {
    sys.surveyed = sys.ring === MAP_RADIUS;
  }
  for (const home of homeworlds) {
    const sys = systems[home];
    if (sys) sys.surveyed = true;
  }

  return { systems, homeworlds };
}

/**
 * Make sure a homeworld has at least two adjacent hexes that can actually be claimed.
 * Converts the least useful neighbours rather than adding new ones, so total map value
 * stays put.
 */
function ensureViableStart(
  systems: Record<HexKey, StarSystem>,
  homeworld: HexKey,
  rng: Rng,
): void {
  const origin = parseKey(homeworld);
  const adjacent = Object.values(systems).filter((s) => distance(origin, s.coord) === 1);
  const developable = adjacent.filter((s) => s.cls !== 'deepSpace' && s.habitability >= 1);
  if (developable.length >= 2) return;

  const voids = adjacent.filter((s) => s.cls === 'deepSpace');
  const needed = 2 - developable.length;
  for (let i = 0; i < needed && i < voids.length; i++) {
    // Ice and Desert are the honest "starter" worlds: modest yield, habitability 2.
    reclass(voids[i]!, rng.chance(0.5) ? 'ice' : 'desert');
  }
}

/** Scatter relics through the Core and Mid Rim, never on a homeworld or in a void. */
function seedRelics(
  systems: Record<HexKey, StarSystem>,
  rng: Rng,
  homeworldSet: Set<HexKey>,
): void {
  const relicKinds: RelicKind[] = ['cache', 'archive', 'foundry', 'seedVault', 'beacon'];

  const candidates = Object.values(systems)
    .filter(
      (s) =>
        !homeworldSet.has(s.id) &&
        s.cls !== 'deepSpace' &&
        s.relic === null &&
        s.ring >= 1 &&
        s.ring <= 3,
    )
    .map((s) => s.id)
    .sort(); // sort before shuffling so the shuffle is seed-deterministic

  rng.shuffle(candidates);

  // The centre already holds one, so place RELIC_COUNT - 1 more.
  const alreadyPlaced = Object.values(systems).filter((s) => s.relic !== null).length;
  const toPlace = Math.max(0, RELIC_COUNT - alreadyPlaced);

  for (let i = 0; i < toPlace && i < candidates.length; i++) {
    const sys = systems[candidates[i]!];
    if (sys) sys.relic = rng.pick(relicKinds);
  }
}

/**
 * Equalise the four starting neighbourhoods.
 *
 * Greedy and deterministic: repeatedly find the poorest and richest neighbourhoods and
 * swap one class between them — upgrading a hex near the poor seat, downgrading one near
 * the rich seat. Homeworlds, relic hexes and the Core are never touched, so the pass
 * cannot flatten the map's authored character.
 */
function balanceStarts(
  systems: Record<HexKey, StarSystem>,
  homeworlds: readonly HexKey[],
  rng: Rng,
): void {
  const homeworldSet = new Set(homeworlds);

  /** Hexes near `home` that the pass is allowed to rewrite. */
  const mutableNear = (home: HexKey): StarSystem[] => {
    const origin = parseKey(home);
    return Object.values(systems)
      .filter((s) => {
        const d = distance(origin, s.coord);
        return (
          d >= 1 &&
          d <= FAIRNESS_RADIUS &&
          !homeworldSet.has(s.id) &&
          s.relic === null &&
          s.ring >= 2 // leave the Core alone
        );
      })
      .sort((a, b) => a.id.localeCompare(b.id));
  };

  // Value ladder the pass moves along, poorest to richest.
  const ladder: SystemClass[] = [
    'deepSpace', 'asteroid', 'volcanic', 'gasGiant', 'ice', 'desert', 'oceanic', 'terran',
  ];

  for (let iteration = 0; iteration < FAIRNESS_MAX_SWAPS; iteration++) {
    const values = homeworlds.map((h) => neighbourhoodValue(systems, h));
    let poorest = 0;
    let richest = 0;
    for (let i = 1; i < values.length; i++) {
      if (values[i]! < values[poorest]!) poorest = i;
      if (values[i]! > values[richest]!) richest = i;
    }
    if (values[richest]! - values[poorest]! <= FAIRNESS_TOLERANCE) return;

    const poorCandidates = mutableNear(homeworlds[poorest]!);
    const richCandidates = mutableNear(homeworlds[richest]!);

    // Upgrade the poorest neighbourhood's weakest eligible hex by one ladder step.
    const upgraded = stepClass(poorCandidates, ladder, +1, rng);
    // Downgrade the richest neighbourhood's strongest eligible hex by one step. Skip a
    // hex shared with the poor seat, or the two moves cancel out.
    const shared = new Set(poorCandidates.map((s) => s.id));
    const downgraded = stepClass(
      richCandidates.filter((s) => !shared.has(s.id)),
      ladder,
      -1,
      rng,
    );

    // Nothing left to trade — the map is as even as this layout allows.
    if (!upgraded && !downgraded) return;
  }
}

/**
 * Move one system in `candidates` one step along the class ladder.
 * `direction` +1 upgrades the weakest, −1 downgrades the strongest. Returns whether a
 * change was made.
 */
function stepClass(
  candidates: readonly StarSystem[],
  ladder: readonly SystemClass[],
  direction: 1 | -1,
  rng: Rng,
): boolean {
  if (candidates.length === 0) return false;

  const rank = (s: StarSystem) => ladder.indexOf(s.cls);
  const eligible = candidates.filter((s) => {
    const idx = rank(s);
    return idx >= 0 && idx + direction >= 0 && idx + direction < ladder.length;
  });
  if (eligible.length === 0) return false;

  // Pick the extreme end, breaking ties randomly so repeated passes do not all pile
  // onto the same hex.
  const target = direction === 1 ? Math.min(...eligible.map(rank)) : Math.max(...eligible.map(rank));
  const tied = eligible.filter((s) => rank(s) === target);
  const chosen = rng.pick(tied);

  reclass(chosen, ladder[rank(chosen) + direction]!);
  return true;
}

/* ---------------------------------------------------------------- diagnostics */

export interface GalaxyReport {
  systemCount: number;
  classCounts: Record<string, number>;
  relicCount: number;
  /** Neighbourhood value per seat — the fairness pass tries to flatten these. */
  startValues: number[];
  startSpread: number;
}

/** Used by the balance harness and the map tests. */
export function reportGalaxy(galaxy: Galaxy): GalaxyReport {
  const classCounts: Record<string, number> = {};
  let relicCount = 0;
  for (const sys of Object.values(galaxy.systems)) {
    classCounts[sys.cls] = (classCounts[sys.cls] ?? 0) + 1;
    if (sys.relic) relicCount++;
  }
  const startValues = galaxy.homeworlds.map((h) => neighbourhoodValue(galaxy.systems, h));
  return {
    systemCount: Object.keys(galaxy.systems).length,
    classCounts,
    relicCount,
    startValues,
    startSpread: Math.max(...startValues) - Math.min(...startValues),
  };
}

/** Seat → homeworld, with a clear error if generation produced too few. */
export function homeworldFor(galaxy: Galaxy, seat: PlayerId): HexKey {
  const home = galaxy.homeworlds[seat];
  if (!home) throw new Error(`Galaxy has no homeworld for seat ${seat}`);
  return home;
}
